import { inject, Injectable } from '@angular/core';
import {
  FirebaseStorage,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytesResumable,
  UploadMetadata,
  UploadTaskSnapshot,
  deleteObject,
} from 'firebase/storage';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { FIREBASE_HANDLES, STORAGE_DOWNLOAD_ORIGIN } from './firebase.providers';

export interface UploadProgress {
  readonly bytesTransferred: number;
  readonly totalBytes: number;
  readonly percent: number;
  readonly state: UploadTaskSnapshot['state'];
}

export interface UploadResult {
  readonly downloadUrl: string;
  readonly path: string;
}

/**
 * Cloud Storage helper. All operations are scoped to the default bucket
 * derived from `environment.firebase.storageBucket`.
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly storage: FirebaseStorage = inject(FIREBASE_HANDLES).storage;

  /**
   * Uploads a file with progress reporting via an Observable. The Observable
   * emits {@link UploadProgress} updates and completes with a single
   * {@link UploadResult} on success.
   */
  upload(
    path: string,
    data: Blob | Uint8Array | ArrayBuffer,
    metadata?: UploadMetadata,
  ): Observable<UploadProgress | UploadResult> {
    const objectRef = ref(this.storage, path);
    const task = uploadBytesResumable(objectRef, data, metadata);

    return new Observable((subscriber) => {
      const unsub = task.on(
        'state_changed',
        (snap) => {
          subscriber.next({
            bytesTransferred: snap.bytesTransferred,
            totalBytes: snap.totalBytes,
            percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
            state: snap.state,
          });
        },
        (err) => subscriber.error(err),
        async () => {
          try {
            const downloadUrl = await getDownloadURL(task.snapshot.ref);
            subscriber.next({ downloadUrl, path });
            subscriber.complete();
          } catch (err) {
            subscriber.error(err);
          }
        },
      );
      return () => unsub();
    });
  }

  getDownloadUrl(path: string): Promise<string> {
    return getDownloadURL(ref(this.storage, path));
  }

  /** A small text object's contents (e.g. a run's `plan.json`), decoded as UTF-8. */
  async getText(path: string): Promise<string> {
    return new TextDecoder().decode(await getBytes(ref(this.storage, path)));
  }

  /**
   * The download URL of an object whose download token is already known —
   * what {@link getDownloadUrl} would return, without its metadata round
   * trip. The token is the access check, so only use one the server handed
   * out through a document the caller could already read.
   */
  tokenUrl(path: string, token: string): string {
    const bucket = environment.firebase.storageBucket;
    return (
      `${STORAGE_DOWNLOAD_ORIGIN}/v0/b/${bucket}/o/${encodeURIComponent(path)}` +
      `?alt=media&token=${encodeURIComponent(token)}`
    );
  }

  delete(path: string): Promise<void> {
    return deleteObject(ref(this.storage, path));
  }
}
